<template>
  <div
    v-if="selectedCategory"
    translate="no"
    id="category-modal-container"
    class="category-modal-container-top-screen"
  >
    <div id="category-modal" class="modal-bigger" ref="categoryModal">
      <div id="header-and-content-category-container" ref="categoryModalContent">
        <div id="edit-category-modal-header">
          <div id="category-name-and-close-btn">
            <h3 id="selected-category-name">{{ selectedCategory.names.translation[language].name}}</h3>
            <div @click="onCloseClick" id="close-category-modal">
              <font-awesome-icon id="category-times" icon="times" />
            </div>
          </div>
        </div>

        <div id="edit-category-content">
          <div id="names-and-descriptions-section" class="form-section">
            <h4 class="section-title">Ονόματα &amp; περιγραφές ανά γλώσσα</h4>
            <div v-for="lang in languages" :key="lang.code" class="language-row">
              <span class="language-badge">{{lang.label}}</span>
              <div class="language-inputs">
                <input
                  type="text"
                  class="text-input"
                  :placeholder="`Όνομα (${lang.label})`"
                  v-model="form.names[lang.code]"
                />
                <textarea
                  class="text-input textarea-input"
                  :placeholder="`Περιγραφή (${lang.label})`"
                  v-model="form.descriptions[lang.code]"
                ></textarea>
              </div>
            </div>
          </div>

          <div id="appearance-section" class="form-section">
            <h4 class="section-title">Εμφάνιση</h4>
            <div class="field-row">
              <label class="field-label">Χρώμα φόντου</label>
              <div class="color-field">
                <input
                  type="color"
                  class="color-picker"
                  :value="bgColorForPicker"
                  @input="form.bgColor = $event.target.value"
                />
                <input
                  type="text"
                  disabled
                  class="text-input color-text"
                  v-model="form.bgColor"
                  placeholder="#ffffff"
                />
              </div>
            </div>
            <div class="field-row">
              <label class="field-label">Χρώμα γραμμάτων</label>
              <select class="text-input color-text" v-model="form.textColor">
                <option value="white">Λευκό</option>
                <option :value="null">Μαύρο</option>
              </select>
            </div>
            <div class="field-row">
              <label class="field-label">Σειρά εμφάνισης</label>
              <select
                class="text-input number-input"
                :disabled="isCreatingCategory"
                v-model.number="form.viewPriority"
              >
                <option
                  v-for="option in viewPriorityOptions"
                  :key="option"
                  :value="option"
                >{{option}}</option>
              </select>
            </div>
            <div class="field-row">
              <label class="field-label">Μέγιστα προϊόντα ανά παραγγελία</label>
              <input
                type="number"
                min="0"
                class="text-input number-input"
                v-model.number="form.maxProductsPerCartForThisCategory"
                placeholder="χωρίς όριο"
              />
            </div>
          </div>

          <div id="options-section" class="form-section">
            <h4 class="section-title">Επιλογές</h4>
            <div class="toggle-row">
              <span class="toggle-label">Απόκρυψη εκτός ωραρίου</span>
              <toggle-button v-model="form.hiddenWhenOutOfTime" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Απόκρυψη εκτός ωραρίου (PDA)</span>
              <toggle-button v-model="form.hiddenWhenOutOfTimePda" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Διαχωριστικό</span>
              <toggle-button v-model="form.separator" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Εκτύπωση διαχωριστικού</span>
              <toggle-button v-model="form.printSeparator" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Μόνο για PDA</span>
              <toggle-button v-model="form.onlyForPda" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Εφαρμογή χρωμάτων και στα προϊόντα</span>
              <toggle-button v-model="form.applyColorsAlsoToProducts" :sync="true" color="#198b4a" />
            </div>
            <div class="toggle-row">
              <span class="toggle-label">Εφαρμογή χρωμάτων και σε mobile οθόνες</span>
              <toggle-button
                v-model="form.applyColorsAlsoToMobileScreens"
                :sync="true"
                color="#198b4a"
              />
            </div>
          </div>

          <div id="schedule-section" class="form-section">
            <h4 class="section-title">Πρόγραμμα ωραρίου</h4>

            <div
              v-for="scheduleGroup in scheduleGroups"
              :key="scheduleGroup.key"
              class="schedule-group"
            >
              <div class="toggle-row">
                <span class="toggle-label">{{scheduleGroup.label}}</span>
                <toggle-button
                  v-model="form[scheduleGroup.key].enabled"
                  :sync="true"
                  color="#198b4a"
                />
              </div>

              <div v-if="form[scheduleGroup.key].enabled" class="schedule-details">
                <div class="toggle-row">
                  <span class="toggle-label">Κοινό ωράριο για όλες τις ημέρες</span>
                  <toggle-button
                    v-model="form[scheduleGroup.key].useCommonSchedule"
                    :sync="true"
                    color="#198b4a"
                  />
                </div>

                <div v-if="form[scheduleGroup.key].useCommonSchedule" class="schedule-day-row">
                  <span class="schedule-day-label">Ώρες</span>
                  <input
                    type="time"
                    class="text-input time-input"
                    v-model="form[scheduleGroup.key].commonStartTime"
                  />
                  <span class="schedule-time-separator">έως</span>
                  <input
                    type="time"
                    class="text-input time-input"
                    v-model="form[scheduleGroup.key].commonEndTime"
                  />
                </div>

                <div v-else>
                  <div v-for="day in days" :key="day.code" class="schedule-day-row">
                    <span class="schedule-day-label">{{day.label}}</span>
                    <input
                      type="time"
                      class="text-input time-input"
                      v-model="form[scheduleGroup.key].perDay[day.code].startTime"
                    />
                    <span class="schedule-time-separator">έως</span>
                    <input
                      type="time"
                      class="text-input time-input"
                      v-model="form[scheduleGroup.key].perDay[day.code].endTime"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="edit-category-modal-footer">
          <p v-if="error" id="error-message">{{errorMessage}}</p>
          <div id="bottom-buttons">
            <button
              id="cancel-category-button"
              class="footer-button"
              :disabled="isSaving"
              @click="onCloseClick"
            >Ακύρωση</button>
            <button
              id="save-category-button"
              class="footer-button"
              :disabled="isSaving"
              @click="onSaveClick"
            >
              <clip-loader v-if="isSaving" :loading="isSaving" size="16px" color="#ffffff" />
              <font-awesome-icon v-else icon="check" />
              {{isSaving ? 'Αποθήκευση...' : 'Αποθήκευση'}}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MenuApi from "../api/menu";
import ClipLoader from "vue-spinner/src/ClipLoader.vue";

const LANGUAGES = [
  { code: "GRE", label: "Ελληνικά" },
  { code: "ENG", label: "Αγγλικά" },
];

const DAYS = [
  { code: "MONDAY", label: "Δευτέρα" },
  { code: "TUESDAY", label: "Τρίτη" },
  { code: "WEDNESDAY", label: "Τετάρτη" },
  { code: "THURSDAY", label: "Πέμπτη" },
  { code: "FRIDAY", label: "Παρασκευή" },
  { code: "SATURDAY", label: "Σάββατο" },
  { code: "SUNDAY", label: "Κυριακή" },
];

const SCHEDULE_GROUPS = [
  { key: "schedule", label: "Πρόγραμμα ωραρίου (QR code)" },
  { key: "pdaSchedule", label: "Πρόγραμμα ωραρίου (PDA)" },
];

export default {
  name: "EditCategoryModal",
  components: {
    ClipLoader,
  },
  props: ["selectedCategory", "restaurantId", "categoriesCount", "isCreatingCategory", "authToken", "jwt"],
  data() {
    return {
      languages: LANGUAGES,
      days: DAYS,
      scheduleGroups: SCHEDULE_GROUPS,
      form: this.buildEmptyForm(),
      isSaving: false,
      error: false,
      errorMessage: "",
    };
  },
  watch: {
    selectedCategory: {
      immediate: true,
      handler(category) {
        this.form = this.isCreatingCategory ? this.buildNewCategoryForm() : this.buildFormFromCategory(category);
        this.error = false;
        this.errorMessage = "";
        this.scrollModalToTop();
      },
    },

    isModalOpen(newVal) {
      if (newVal) {
        this.form = this.isCreatingCategory ? this.buildNewCategoryForm() : this.buildFormFromCategory(this.selectedCategory);
        this.error = false;
        this.errorMessage = "";
        this.scrollModalToTop();
      }
    },
  },
  computed: {
    language() {
      return this.$store.getters.getLanguage;
    },

    isModalOpen() {
      return this.$store.state.editCategoryModalOpen;
    },

    bgColorForPicker() {
      return /^#[0-9a-fA-F]{6}$/.test(this.form.bgColor) ? this.form.bgColor : "#ffffff";
    },

    viewPriorityOptions() {
      let categoriesCount = this.categoriesCount || 0;
      let options = [];
      for (let i = 1; i <= categoriesCount; i++) {
        options.push(i);
      }
      if (this.form.viewPriority != null && !options.includes(this.form.viewPriority)) {
        options.push(this.form.viewPriority);
        options.sort((a, b) => a - b);
      }
      return options;
    },
  },
  methods: {
    buildEmptyForm() {
      return {
        productCategoryId: null,
        names: { GRE: "", ENG: "", DEU: "", ITA: "", FRA: "" },
        descriptions: { GRE: "", ENG: "", DEU: "", ITA: "", FRA: "" },
        hiddenWhenOutOfTime: false,
        hiddenWhenOutOfTimePda: false,
        separator: false,
        printSeparator: false,
        onlyForPda: false,
        applyColorsAlsoToProducts: false,
        applyColorsAlsoToMobileScreens: false,
        maxProductsPerCartForThisCategory: null,
        bgColor: "#ffffff",
        textColor: null,
        viewPriority: 0,
        schedule: this.buildEmptyScheduleForm(),
        pdaSchedule: this.buildEmptyScheduleForm(),
      };
    },

    buildNewCategoryForm() {
      let form = this.buildEmptyForm();
      form.applyColorsAlsoToProducts = true;
      form.applyColorsAlsoToMobileScreens = true;
      form.viewPriority = (this.categoriesCount || 0) + 1;
      return form;
    },

    buildDefaultPerDaySchedule(startTime, endTime) {
      let perDay = {};
      DAYS.forEach((day) => {
        perDay[day.code] = { startTime: startTime || "09:00", endTime: endTime || "23:00" };
      });
      return perDay;
    },

    buildEmptyScheduleForm() {
      return {
        enabled: false,
        useCommonSchedule: true,
        commonStartTime: "09:00",
        commonEndTime: "23:00",
        perDay: this.buildDefaultPerDaySchedule(),
      };
    },

    toTimeInputValue(apiTime) {
      return apiTime ? apiTime.substring(0, 5) : "09:00";
    },

    toApiTime(timeInputValue) {
      return `${timeInputValue}:00`;
    },

    findDaySchedule(schedules, dayCode) {
      if (!schedules) {
        return null;
      }
      if (Array.isArray(schedules)) {
        return schedules.find((entry) => entry.day === dayCode) || null;
      }
      return schedules[dayCode] || null;
    },

    buildScheduleFormFromCategorySchedules(schedules) {
      let hasSchedules = Array.isArray(schedules) ? schedules.length > 0 : !!(schedules && Object.keys(schedules).length > 0);
      if (!hasSchedules) {
        return this.buildEmptyScheduleForm();
      }

      let perDay = {};
      this.days.forEach((day) => {
        let daySchedule = this.findDaySchedule(schedules, day.code);
        perDay[day.code] = {
          startTime: daySchedule && daySchedule.startTime ? this.toTimeInputValue(daySchedule.startTime) : "09:00",
          endTime: daySchedule && daySchedule.endTime ? this.toTimeInputValue(daySchedule.endTime) : "23:00",
        };
      });

      let uniqueRanges = new Set(this.days.map((day) => `${perDay[day.code].startTime}-${perDay[day.code].endTime}`));

      return {
        enabled: true,
        useCommonSchedule: uniqueRanges.size === 1,
        commonStartTime: perDay.MONDAY.startTime,
        commonEndTime: perDay.MONDAY.endTime,
        perDay,
      };
    },

    buildSchedulesPayload(scheduleForm) {
      if (!scheduleForm.enabled) {
        return null;
      }

      let schedules = {};
      this.days.forEach((day) => {
        let startTime = scheduleForm.useCommonSchedule ? scheduleForm.commonStartTime : scheduleForm.perDay[day.code].startTime;
        let endTime = scheduleForm.useCommonSchedule ? scheduleForm.commonEndTime : scheduleForm.perDay[day.code].endTime;
        schedules[day.code] = {
          startTime: this.toApiTime(startTime),
          endTime: this.toApiTime(endTime),
        };
      });
      return schedules;
    },

    buildFormFromCategory(category) {
      let emptyForm = this.buildEmptyForm();
      if (!category) {
        return emptyForm;
      }

      let names = { ...emptyForm.names };
      let descriptions = { ...emptyForm.descriptions };
      this.languages.forEach((lang) => {
        let translation = category.names && category.names.translation ? category.names.translation[lang.code] : null;
        names[lang.code] = translation && translation.name ? translation.name : "";
        descriptions[lang.code] = translation && translation.description ? translation.description : "";
      });

      return {
        productCategoryId: category.categoryId,
        names,
        descriptions,
        hiddenWhenOutOfTime: !!category.hiddenWhenOutOfTime,
        hiddenWhenOutOfTimePda: !!category.hiddenWhenOutOfTimePda,
        separator: !!category.separator,
        printSeparator: !!category.printSeparator,
        onlyForPda: !!category.onlyForPda,
        applyColorsAlsoToProducts: !!category.applyColorsAlsoToProducts,
        applyColorsAlsoToMobileScreens: !!category.applyColorsAlsoToMobileScreens,
        maxProductsPerCartForThisCategory:
          category.maxProductsPerCartForThisCategory != null ? category.maxProductsPerCartForThisCategory : null,
        bgColor: category.bgColor || "#ffffff",
        textColor: category.textColor || null,
        viewPriority: category.viewPriority != null ? category.viewPriority : 0,
        schedule: this.buildScheduleFormFromCategorySchedules(category.schedules),
        pdaSchedule: this.buildScheduleFormFromCategorySchedules(category.pdaSchedules),
      };
    },

    onCloseClick() {
      this.$store.dispatch("closeEditCategoryModal");
    },

    scrollModalToTop() {
      try {
        this.$nextTick(() => {
          if (this.$refs.categoryModal) {
            this.$refs.categoryModal.scrollTop = 0;
          }
          if (this.$refs.categoryModalContent) {
            this.$refs.categoryModalContent.scrollTop = 0;
          }
        });
      } catch (error) {
        console.log("Could not scroll category modal to top");
      }

    },

    async onSaveClick() {
      if (this.isSaving) {
        return;
      }

      if (!this.form.names.GRE || this.form.names.GRE.trim().length === 0) {
        this.error = true;
        this.errorMessage = "Το ελληνικό όνομα της κατηγορίας είναι υποχρεωτικό";
        return;
      }

      this.error = false;
      this.errorMessage = "";
      this.isSaving = true;

      let names = { ...this.form.names };
      let descriptions = { ...this.form.descriptions };
      names.ENG = names.ENG && names.ENG.trim().length > 0 ? names.ENG : names.GRE;
      descriptions.ENG = descriptions.ENG && descriptions.ENG.trim().length > 0 ? descriptions.ENG : descriptions.GRE;

      let payload = {
        productCategoryId: this.form.productCategoryId,
        names: names,
        descriptions: descriptions,
        hiddenWhenOutOfTime: this.form.hiddenWhenOutOfTime,
        hiddenWhenOutOfTimePda: this.form.hiddenWhenOutOfTimePda,
        separator: this.form.separator,
        printSeparator: this.form.printSeparator,
        onlyForPda: this.form.onlyForPda,
        applyColorsAlsoToProducts: this.form.applyColorsAlsoToProducts,
        applyColorsAlsoToMobileScreens: this.form.applyColorsAlsoToMobileScreens,
        maxProductsPerCartForThisCategory:
          this.form.maxProductsPerCartForThisCategory === "" ? null : this.form.maxProductsPerCartForThisCategory,
        bgColor: this.form.bgColor,
        textColor: this.form.textColor,
        viewPriority: this.form.viewPriority,
        schedules: this.buildSchedulesPayload(this.form.schedule),
        pdaSchedules: this.buildSchedulesPayload(this.form.pdaSchedule),
      };

      try {
        if (this.isCreatingCategory) {
          await MenuApi.createCategory(this.restaurantId, payload, this.authToken, this.jwt);
        } else {
          await MenuApi.editCategory(this.restaurantId, payload, this.authToken, this.jwt);
        }
        this.$emit("category-updated");
        this.$store.dispatch("closeEditCategoryModal");
      } catch (error) {
        this.error = true;
        this.errorMessage =
          error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Παρουσιάστηκε σφάλμα κατά την αποθήκευση της κατηγορίας";
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
#category-modal-container {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  display: flex;
  justify-content: center;
  z-index: 205 !important;
}

.category-modal-container-top-screen {
  margin-top: 1vh;
  align-items: flex-start;
}

#category-modal {
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 98vw;
  height: 98vh;
  min-width: 200px;
  min-height: 97%;
  max-height: 95%;
  background: #f5f5f5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.16), 0 2px 4px rgba(0, 0, 0, 0.23);
  overflow-y: scroll;

  opacity: 1;
  border-radius: 12px;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  z-index: 202 !important;
}

#category-modal::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

#header-and-content-category-container {
  overflow-x: hidden;
}

#close-category-modal {
  position: sticky;
  top: 2%;
  left: 93%;
  background: white;
  border-radius: 100%;
  min-height: 4.5vh;
  height: 40% !important;
  width: 4.5vh;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-top: 4px;
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.24), 2px 2px 4px rgba(0, 0, 0, 0.36);
}

#edit-category-modal-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-evenly;
  flex-shrink: 0;
  width: 100%;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.24), 0 2px 4px rgba(0, 0, 0, 0.36);
  position: sticky;
  top: 0;
  z-index: 20;
  padding-bottom: 10px;
}

#category-name-and-close-btn {
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: flex-end;
  justify-content: space-between;
}

#edit-category-content {
  display: flex;
  flex-direction: column;
  padding: 12px 6px 90px 6px;
}

#error-message {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #d9534f;
}

.form-section {
  background: white;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1), 2px 2px 4px rgba(0, 0, 0, 0.14);
}

.section-title {
  margin: 0 0 10px 0;
  font-size: 15px;
  font-weight: 700;
  color: #003340;
}

.language-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

.language-row:last-child {
  margin-bottom: 0;
}

.language-badge {
  flex-shrink: 0;
  width: 84px;
  padding: 4px 6px;
  border-radius: 6px;
  background: #003340;
  color: white;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}

.language-inputs {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-grow: 1;
}

.text-input {
  box-shadow: inset 0px 0px 2px rgba(116, 135, 150, 0.2),
    inset 1px 1px 2px rgba(116, 135, 150, 0.36);
  border-radius: 6px;
  border: none;
  padding: 6px 8px;
  font-size: 14px;
  background: #f7f7f7;
  width: 100%;
  box-sizing: border-box;
}

.text-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.textarea-input {
  resize: vertical;
  min-height: 44px;
  font-family: inherit;
}

.field-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.field-row:last-child {
  margin-bottom: 0;
}

.field-label {
  width: 220px;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  color: #343a40;
}

.color-field {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.color-picker {
  width: 40px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.color-text,
.number-input {
  max-width: 140px;
}

select.text-input {
  -webkit-appearance: auto;
  cursor: pointer;
}

.toggle-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #ececec;
}

.toggle-row:last-child {
  border-bottom: none;
}

.toggle-label {
  font-size: 14px;
  font-weight: 600;
  color: #343a40;
}

.schedule-group {
  margin-bottom: 14px;
}

.schedule-group:last-child {
  margin-bottom: 0;
}

.schedule-details {
  padding-left: 12px;
  margin-top: 6px;
  border-left: 3px solid #ececec;
}

.schedule-day-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}

.schedule-day-label {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: #343a40;
}

.time-input {
  max-width: 110px;
}

.schedule-time-separator {
  font-size: 13px;
  color: #6c757d;
}

#edit-category-modal-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 12px 16px;
  background: white;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.14);
  z-index: 20;
}

#bottom-buttons {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 10px;
}

.footer-button {
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.footer-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

#cancel-category-button {
  background: #e2e2e2;
  color: #343a40;
}

#save-category-button {
  background: #17b86e;
  color: white;
}

@media screen and (max-width: 1018px) {
  #close-category-modal {
    width: 4.5vh;
    min-height: 4.5vh;
    top: 1%;
    margin-right: 8px;
  }

  #selected-category-name {
    margin: 10px 8px 0 8px;
  }
}

@media screen and (max-width: 753px) {
  #selected-category-name {
    font-size: 18px;
  }

  .field-label {
    width: 160px;
  }
}

@media screen and (max-width: 350px) {
  #selected-category-name {
    font-size: 16px;
    margin-right: 13.5%;
  }

  #close-category-modal {
    min-height: 3.5vh;
    width: 3.5vh;
    margin-right: 5%;
  }

  #category-times {
    font-size: 0.85rem;
  }

  .language-row {
    flex-direction: column;
  }

  .language-badge {
    width: auto;
  }

  .field-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .field-label {
    width: auto;
  }

  .schedule-day-row {
    flex-wrap: wrap;
  }
}

@media screen and (max-height: 300px) {
  #category-modal {
    min-height: 100%;
  }

  #selected-category-name {
    font-size: 15px;
  }
}
</style>
